export const settingsNames = {
  agency: {
    client: {
      billing: {
        defaultCurrency: 'client.billing.invoicing.defaultCurrency',
        paymentTerms: 'client.billing.defaultPaymentTerms'
      }
    }
  },
  global: {
    TBA: 'global.TBA'
  },
  user: {
    TBA: 'user.TBA'
  }
} as const

export type AgencySettingName =
  (typeof settingsNames.agency.client.billing)[keyof typeof settingsNames.agency.client.billing]

export type GlobalSettingName =
  (typeof settingsNames.global)[keyof typeof settingsNames.global]

export type UserSettingName =
  (typeof settingsNames.user)[keyof typeof settingsNames.user]

export type SettingName = AgencySettingName
/* | GlobalSettingName
  | UserSettingName */

export type TypedSettingValues = {
  'client.billing.invoicing.defaultCurrency': string
  'client.billing.defaultPaymentTerms': {
    paymentPeriodInDays: number
    useEndOfMonthTerms: boolean
  }
}

// Fallback type for settings that don't have a specific type defined
export type UntypedSettingValue = null

export function isAgencySettingsCode(code: string): code is AgencySettingName {
  return Object.values(settingsNames.agency.client.billing).some(
    (value) => value === code
  )
}

export function isGlobalSettingsCode(code: string): code is GlobalSettingName {
  return Object.values(settingsNames.global).some((value) => value === code)
}

export function isUserSettingsCode(code: string): code is UserSettingName {
  return Object.values(settingsNames.user).some((value) => value === code)
}

export function isSettingsCode(code: string): code is SettingName {
  // SettingName is currently aliased to AgencySettingName (see the
  // commented-out | GlobalSettingName | UserSettingName lines above);
  // when that union is widened back out, this body has to widen too.
  return isAgencySettingsCode(code)
}

export function isStringArrayOfSettingsCodes(
  codes: string[]
): codes is SettingName[] {
  return codes.every((code) => isSettingsCode(code))
}

export interface EffectiveSettingDirect<TValue> {
  value: TValue
  valueStatus: 'direct'
  permissionsMissing: string[]
}

export interface EffectiveSettingInherited<TValue> {
  value: TValue
  valueStatus: 'inherited'
  permissionsMissing: string[]
}

export interface EffectiveSettingMissing {
  value: null
  valueStatus: 'missing'
  permissionsMissing: string[]
}

export interface EffectiveSettingForbidden {
  value: null
  valueStatus: 'forbidden'
  permissionsMissing: string[]
}

export type EffectiveSetting<TValue> =
  | EffectiveSettingDirect<TValue>
  | EffectiveSettingInherited<TValue>
  | EffectiveSettingMissing
  | EffectiveSettingForbidden

// Helper type to map a setting name to its EffectiveSetting type
export type MapSettingToEffectiveSetting<T extends SettingName> =
  T extends keyof TypedSettingValues
    ? EffectiveSetting<TypedSettingValues[T]>
    : EffectiveSetting<UntypedSettingValue>

// Type that takes a tuple of setting names and returns a mapped record
// where each setting name maps to its corresponding EffectiveSetting type
// Example: EffectiveSettings<["client.billing.defaultPaymentTerms", "client.billing.invoicing.defaultCurrency"]>
// returns: {
//   "client.billing.defaultPaymentTerms": EffectiveSetting<DefaultPaymentTermsValue>
//   "client.billing.invoicing.defaultCurrency": EffectiveSetting<DefaultCurrencyValue>
// }
export type EffectiveSettings<T extends readonly SettingName[]> = {
  [K in T[number]]: MapSettingToEffectiveSetting<K>
}

// Type that extracts just the value type for a single setting name
// Example: EffectiveSettingValue<'client.billing.defaultPaymentTerms'>
// returns: { paymentPeriodInDays: number; useEndOfMonthTerms: boolean }
export type EffectiveSettingValue<TSettingName extends SettingName> =
  TSettingName extends keyof TypedSettingValues
    ? TypedSettingValues[TSettingName]
    : UntypedSettingValue

export interface LayeredSetting<TValue> {
  layerScope: 'default' | 'agency' | 'global' | 'user'
  value: TValue
  scopeEntityId: string | null
  scopeEntityName: string | null
}

export interface ScopedSettingAssigned<TValue> {
  value: TValue
  layerStatus: 'assigned'
  permissionsMissing: string[]
  parentChain: LayeredSetting<TValue>[]
}

export interface ScopedSettingInherited<TValue> {
  value: null
  layerStatus: 'inherited'
  permissionsMissing: string[]
  parentChain: LayeredSetting<TValue>[]
}

export interface ScopedSettingForbidden {
  value: null
  layerStatus: 'forbidden'
  permissionsMissing: string[]
  parentChain: LayeredSetting<null>[]
}

export type ScopedSetting<TValue> =
  | ScopedSettingAssigned<TValue>
  | ScopedSettingInherited<TValue>
  | ScopedSettingForbidden

// Helper type to map a setting name to its EffectiveSetting type
export type MapSettingToScopedSetting<T extends SettingName> =
  T extends keyof TypedSettingValues
    ? ScopedSetting<TypedSettingValues[T]>
    : ScopedSetting<UntypedSettingValue>

// Type that takes a tuple of setting names and returns a mapped record
// where each setting name maps to its corresponding EffectiveSetting type
// Example: EffectiveSettings<["client.billing.defaultPaymentTerms", "client.billing.invoicing.defaultCurrency"]>
// returns: {
//   "client.billing.defaultPaymentTerms": EffectiveSetting<DefaultPaymentTermsValue>
//   "client.billing.invoicing.defaultCurrency": EffectiveSetting<DefaultCurrencyValue>
// }
export type ScopedSettings<T extends readonly SettingName[]> = {
  [K in T[number]]: MapSettingToScopedSetting<K>
}

// Type that extracts just the value type for a single setting name
// Example: ScopedSettingValue<'client.billing.defaultPaymentTerms'>
// returns: { paymentPeriodInDays: number; useEndOfMonthTerms: boolean }
export type ScopedSettingValue<TSettingName extends SettingName> =
  TSettingName extends keyof TypedSettingValues
    ? TypedSettingValues[TSettingName]
    : UntypedSettingValue
